import { z } from 'zod';
import { ClientPaymentAllocationSchema, DepositAdjustmentReasonSchema } from './client-payment';
import { FxConversionSchema, LedgerDirectionSchema, LedgerMethodSchema, PaymentCurrencySchema } from './payment';
/**
 * Admin auction-control audit contract.
 *
 * The three admin actions that can change WHO WINS a lot or WHAT A CLIENT MUST
 * PAY are, by policy, never unaccountable: each writes one of these events to
 * the transactional outbox IN THE SAME MongoDB transaction as the mutation it
 * describes — exactly the guarantee `RelayAuditEvent` gives the relay desk
 * (see relay.ts + modules/relay/relay-queue.service.ts).
 *
 * These events are AUDIT records, not realtime fanout: the `bid.status_changed`
 * messages already on `sse:admin:bids` carry the new document but no actor, so
 * they cannot answer "which admin did this, and when". `who` + `when` are
 * mandatory here for that reason.
 */
/** Admin auction-control actions that must leave an audit trail. */
export const AdminAuctionAuditTypeSchema = z.enum([
    /** PATCH /api/admin/cars/:carId/minimum-bid — admin raised the lot's floor. */
    'admin.minimum_bid_set',
    /** POST /api/admin/cars/:carId/cancel-all-bids — admin voided every live bid on a lot. */
    'admin.bids_cancelled',
    /** PATCH /api/bids/:bidId/status {status:'won'} — admin decided the lot's winner. */
    'admin.winner_set',
]);
export const ADMIN_AUCTION_AUDIT_TYPES = AdminAuctionAuditTypeSchema.options;
/**
 * One admin auction-control audit event. The `data` union is flat (nullish
 * per-action fields) rather than discriminated so the outbox `message` stays a
 * plain `Record<string, unknown>` — same shape choice as RelayAuditEvent.
 */
export const AdminAuctionAuditEventSchema = z.object({
    type: AdminAuctionAuditTypeSchema,
    data: z.object({
        /** Lot the action applied to (always present — every action is car-scoped). */
        carId: z.string(),
        /** Admin who performed the action (AuthenticatedUser.userId). */
        who: z.string(),
        /** ISO timestamp of the action. */
        when: z.string(),
        // ── admin.minimum_bid_set ───────────────────────────────────────────────
        /** Effective minimum before the change. */
        previousMinimum: z.number().nullish(),
        /** Effective minimum after the change (max of the admin value and the top bid). */
        newMinimum: z.number().nullish(),
        /** The raw value the admin typed, before the top-bid floor was applied. */
        adminSetMinimum: z.number().nullish(),
        /** Active bids demoted to Outbid because they fell below the new floor. */
        outbidBidsCount: z.number().nullish(),
        // ── admin.bids_cancelled ────────────────────────────────────────────────
        /** Bids moved to Cancelled by this action. */
        cancelledBidIds: z.array(z.string()).nullish(),
        /** Convenience count of `cancelledBidIds`. */
        cancelledCount: z.number().nullish(),
        // ── admin.winner_set ────────────────────────────────────────────────────
        /** The bid promoted to Won. */
        winningBidId: z.string().nullish(),
        /** Owner of the winning bid. */
        winningUserId: z.string().nullish(),
        /** Amount of the winning bid, in CHF. */
        winningAmount: z.number().nullish(),
        /** True when the admin knowingly replaced an existing winner. */
        forceReplace: z.boolean().nullish(),
        /** Bids that held Won before this action and were demoted by it. */
        replacedWinnerBidIds: z.array(z.string()).nullish(),
    }),
});
/**
 * One changed leaf of a pricing rule set, as the audit renders it. `from` is
 * null on the very first published version.
 */
export const PricingRuleDiffEntrySchema = z.object({
    /** Dotted path into the rule set, e.g. "plans.retail.marginalBands.0.pct". */
    path: z.string(),
    from: z.unknown().nullish(),
    to: z.unknown().nullish(),
});
/**
 * `PUT /api/admin/pricing/rules` — the owner published a new tariff version.
 *
 * NOT part of {@link AdminAuctionAuditEventSchema}: that schema is car-scoped
 * (every event carries a `carId`) and a tariff change is scoped to nothing
 * smaller than the whole platform. It belongs in the same audit trail for the
 * same reason, though — publishing a rule set changes WHAT EVERY CLIENT PAYS,
 * and that can never be anonymous.
 */
export const AdminPricingAuditEventSchema = z.object({
    type: z.literal('admin.pricing_rules_changed'),
    data: z.object({
        /** Admin who published it (AuthenticatedUser.userId). */
        who: z.string(),
        /** ISO timestamp of the publication. */
        when: z.string(),
        /** `customs.version` that was in force before (null on first publish). */
        previousVersion: z.string().nullable(),
        /** `customs.version` of the document just inserted. */
        newVersion: z.string(),
        /** Every leaf that moved, so the trail answers "what changed", not just "when". */
        diff: z.array(PricingRuleDiffEntrySchema),
    }),
});
/** Outbox `type` of {@link AdminPricingAuditEventSchema}. */
export const ADMIN_PRICING_RULES_CHANGED = 'admin.pricing_rules_changed';
/**
 * `PATCH /api/admin/users/:id/exposure` — an admin changed a client's
 * solvency settings (deposit, exposure cap, exemption). Platform money rule,
 * user-scoped: like the tariff event it is not car-scoped, and like every
 * admin action that changes WHAT A CLIENT MAY OWE it is never anonymous.
 */
export const AdminExposureAuditEventSchema = z.object({
    type: z.literal('admin.exposure_changed'),
    data: z.object({
        /** The client whose settings changed. */
        userId: z.string(),
        /** Admin who performed the action (AuthenticatedUser.userId). */
        who: z.string(),
        /** ISO timestamp of the action. */
        when: z.string(),
        previous: z.object({
            depositChf: z.number(),
            exposureLimitChf: z.number().nullable(),
            exposureExempt: z.boolean(),
        }),
        next: z.object({
            depositChf: z.number(),
            exposureLimitChf: z.number().nullable(),
            exposureExempt: z.boolean(),
        }),
    }),
});
/** Outbox `type` of {@link AdminExposureAuditEventSchema}. */
export const ADMIN_EXPOSURE_CHANGED = 'admin.exposure_changed';
/**
 * `POST /api/admin/users/:userId/payments` — an admin recorded money a client
 * paid and allocated it to the deposit and/or invoices. Written in the SAME
 * transaction as the ledger row / settlements it describes. The whole-payment
 * conversion (`amount` in `currency` → `amountChf`, `fx`) lives here, because a
 * split payment's ledger row carries only its own part.
 */
export const AdminClientPaymentAuditEventSchema = z.object({
    type: z.literal('admin.client_payment_recorded'),
    data: z.object({
        /** The client who paid. */
        userId: z.string(),
        /** Admin who recorded it (AuthenticatedUser.userId). */
        who: z.string(),
        /** ISO timestamp of the recording. */
        when: z.string(),
        paymentId: z.string(),
        amount: z.number().finite().positive(),
        currency: PaymentCurrencySchema,
        amountChf: z.number().finite().positive(),
        fx: FxConversionSchema.nullable(),
        method: LedgerMethodSchema,
        reference: z.string().nullable(),
        /** ISO date the money was received (operator's record). */
        receivedAt: z.string(),
        note: z.string().nullable(),
        allocations: z.array(ClientPaymentAllocationSchema).min(1),
    }),
});
/** Outbox `type` of {@link AdminClientPaymentAuditEventSchema}. */
export const ADMIN_CLIENT_PAYMENT_RECORDED = 'admin.client_payment_recorded';
/**
 * `POST /api/admin/users/:userId/deposit/movements` with `kind: 'adjustment'` —
 * an admin corrected a client's deposit by hand («Коригування»). Rare, and
 * never anonymous: the reason is mandatory (≥ 10 characters) and the balance
 * before and after is recorded.
 */
export const AdminDepositAdjustmentAuditEventSchema = z.object({
    type: z.literal('admin.deposit_adjusted'),
    data: z.object({
        userId: z.string(),
        who: z.string(),
        when: z.string(),
        direction: LedgerDirectionSchema,
        amountChf: z.number().finite().positive(),
        reason: DepositAdjustmentReasonSchema,
        depositBeforeChf: z.number().finite(),
        depositAfterChf: z.number().finite(),
    }),
});
/** Outbox `type` of {@link AdminDepositAdjustmentAuditEventSchema}. */
export const ADMIN_DEPOSIT_ADJUSTED = 'admin.deposit_adjusted';
// ── Phase 13: the audit READ surface ─────────────────────────────────────────
/** Hard ceiling on one audit page; the default is a screenful. */
export const AUDIT_QUERY_MAX_LIMIT = 200;
export const AUDIT_QUERY_DEFAULT_LIMIT = 50;
/**
 * `GET /api/admin/audit?bidId=&carId=&who=&from=&to=&limit=` — PARSED, never
 * cast: every filter is a plain string or a date, `limit` is bounded, and an
 * unknown key is rejected rather than ignored (a typo in `bidid` must not
 * silently return the whole log).
 */
export const AuditQuerySchema = z
    .object({
    bidId: z.string().trim().min(1).max(64).optional(),
    carId: z.string().trim().min(1).max(64).optional(),
    /** Admin userId that performed the action (`data.who`). */
    who: z.string().trim().min(1).max(64).optional(),
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
    limit: z.coerce.number().int().min(1).max(AUDIT_QUERY_MAX_LIMIT).default(AUDIT_QUERY_DEFAULT_LIMIT),
})
    .strict();
/**
 * One row of the admin audit log — an `outbox_events` document reduced to
 * what an operator reads: who did what to which bid/lot, when, and the
 * event's own data. The admin firehose mirror (`fullDocument`) is never
 * returned; it carries the whole bid document and notifies nobody.
 */
export const AuditEntrySchema = z.object({
    /** The outbox `_id`. */
    id: z.string(),
    type: z.string(),
    aggregate: z.string().nullish(),
    /** The outbox `createdAt`, ISO-8601. */
    at: z.string(),
    /** Actor (admin userId) when the row names one; null for platform-driven rows. */
    who: z.string().nullable(),
    /**
     * The actor as an operator reads them: the admin's MASKED email
     * (`j***@x.com` — never a full address on the wire), falling back to the
     * raw `who` id when it resolves to nobody. Null exactly when `who` is null.
     */
    whoLabel: z.string().nullable(),
    bidId: z.string().nullable(),
    carId: z.string().nullable(),
    userId: z.string().nullable(),
    /** The event payload (flattened `data` or the flat row itself), verbatim. */
    data: z.record(z.unknown()),
    /**
     * The channel the action came through when it was not the web admin
     * (admin-mobile-app phase 4): `'admin-mobile'` for a request made with the
     * admin app's access token. Absent on every other row.
     */
    via: z.string().optional(),
    /** The `admin_device_sessions` id the phone acted under; present with `via`. */
    deviceSessionId: z.string().optional(),
});
//# sourceMappingURL=admin-audit.js.map